'use client'

import { Icon } from '@iconify/react'
import { useReducedMotion } from 'framer-motion'
import { Avatar, AvatarFallback, AvatarImage, Orbital, OrbitalCenter } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import { CodeWindow } from '../code-window'

export interface FeaturesAgentTeamMember {
  name: string
  avatar: string
}

export interface FeaturesAgentTeamProps {
  title?: string
  description?: string
  /** AI agent avatars to orbit around the integration */
  agents?: FeaturesAgentTeamMember[]
  /** Integration icon (Iconify name or URL) */
  integrationIcon?: string
  /** Code to display in the code window */
  code?: string
  /** Programming language for syntax highlighting */
  language?: string
  /** Orbit radius in pixels */
  orbitRadius?: number
  /** Orbit speed multiplier */
  orbitSpeed?: number
  className?: string
}

const defaultAgents: FeaturesAgentTeamMember[] = [
  { name: 'Sam', avatar: '/avatars/Sam.png' },
  { name: 'Priya', avatar: '/avatars/Priya.png' },
  { name: 'Tom', avatar: '/avatars/Tom.png' },
  { name: 'Archie', avatar: '/avatars/Archie.png' },
  { name: 'Ralph', avatar: '/avatars/Ralph.png' },
  { name: 'Mei', avatar: '/avatars/Mei.png' },
  { name: 'Zara', avatar: '/avatars/Zara.png' },
]

const defaultCode = `import { on } from 'workflows.do'
import { priya, ralph, tom, quinn, mark, sally } from 'agents.do'

on.Idea.captured(async idea => {
  const product = await priya\`brainstorm \${idea}\`
  const backlog = await priya.plan(product)

  for (const issue of backlog.ready) {
    const pr = await ralph\`implement \${issue}\`

    do await ralph\`update \${pr}\`
    while (!await pr.approvedBy(quinn, tom, priya))

    await pr.merge()
  }

  await mark\`document and launch \${product}\`
  await sally\`start outbound for \${product}\`
})`

/**
 * FeaturesAgentTeam - AI agent team capability showcase
 *
 * Shows multiple AI agents orbiting a central provider (e.g., GitHub),
 * demonstrating team collaboration with a code window.
 *
 * Best for: AI team/swarm landing pages, multi-agent orchestration (Services/Agents dialect)
 */
export function FeaturesAgentTeam({
  title = 'AI Agent Team',
  description = 'Deploy a team of specialized AI agents that collaborate seamlessly. Each agent handles specific tasks while working together through your existing tools and workflows.',
  agents = defaultAgents,
  integrationIcon = 'logos:github-icon',
  code = defaultCode,
  language = 'typescript',
  orbitRadius = 100,
  orbitSpeed = 0.5,
  className,
}: FeaturesAgentTeamProps) {
  const prefersReducedMotion = useReducedMotion()

  // Split agents between two rings (3 inner, rest outer)
  const innerAgents = agents.slice(0, 3)
  const outerAgents = agents.slice(3)
  const innerRadius = orbitRadius
  const outerRadius = orbitRadius + 50
  const orbitalSize = (outerRadius + 50) * 2

  return (
    <section className={cn('relative bg-background py-32', className)}>
      <div className="container mx-auto relative flex flex-col items-center gap-5">
        {/* Orbital at top, title overlaps bottom of orbital */}
        <div className="relative flex flex-col items-center">
          {/* Orbital container - at the top */}
          <div
            className="relative"
            style={{ height: orbitalSize, width: orbitalSize }}
          >
            {/* Inner ring */}
            <Orbital
              radius={innerRadius}
              speed={orbitSpeed}
              itemSize={48}
              showPath={!prefersReducedMotion}
            >
              {innerAgents.map((agent) => (
                <div
                  key={agent.name}
                  className="flex size-12 items-center justify-center rounded-full border border-border bg-gradient-to-b from-background to-muted shadow-[0_1px_0_0_rgba(255,255,255,0.1)_inset,0_4px_6px_-1px_rgba(0,0,0,0.1),0_8px_16px_-4px_rgba(0,0,0,0.1)]"
                >
                  <Avatar className="size-10">
                    <AvatarImage src={agent.avatar} alt={agent.name} />
                    <AvatarFallback className="text-xs font-semibold">
                      {agent.name.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>
              ))}
            </Orbital>

            {/* Outer ring */}
            <Orbital
              radius={outerRadius}
              speed={orbitSpeed * 0.7}
              itemSize={48}
              showPath={!prefersReducedMotion}
              reverse
            >
              {outerAgents.map((agent) => (
                <div
                  key={agent.name}
                  className="flex size-12 items-center justify-center rounded-full border border-border bg-gradient-to-b from-background to-muted shadow-[0_1px_0_0_rgba(255,255,255,0.1)_inset,0_4px_6px_-1px_rgba(0,0,0,0.1),0_8px_16px_-4px_rgba(0,0,0,0.1)]"
                >
                  <Avatar className="size-10">
                    <AvatarImage src={agent.avatar} alt={agent.name} />
                    <AvatarFallback className="text-xs font-semibold">
                      {agent.name.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>
              ))}
            </Orbital>

            {/* Center icon */}
            <OrbitalCenter pulse={!prefersReducedMotion}>
              {integrationIcon.startsWith('http') || integrationIcon.startsWith('/') ? (
                <img src={integrationIcon} alt="Integration" className="size-8 dark:invert" />
              ) : (
                <Icon icon={integrationIcon} className="size-8 dark:invert" />
              )}
            </OrbitalCenter>
          </div>

          {/* Title and description - overlaps bottom of orbital, in front */}
          <div className="relative z-10 -mt-32 bg-linear-to-t from-background from-60% to-transparent pt-16 text-center">
            <h2 className="text-pretty text-3xl font-semibold lg:text-4xl">
              {title}
            </h2>
            <div className="mx-auto mt-4 max-w-3xl">
              <Markdown size="base">{description}</Markdown>
            </div>
          </div>
        </div>

        {/* Code Window */}
        <div className="relative mt-8 w-full max-w-4xl md:mt-10">
          <CodeWindow
            code={code}
            language={language}
            showHeader={false}
            className="rounded-[var(--radius-xl,calc(var(--radius)*1.5))]"
          />
        </div>
      </div>
    </section>
  )
}
